import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { useProgramme } from "../../hooks/useProgramme";
import api from "../../services/api";
import {
  deserializeSession,
  withoutRecordedTimes,
} from "../../utils/sessionShare";
import {
  COPY_PLAN_LABEL,
} from "../session/CopySessionMenu";
import ShareSessionSheet from "../share/ShareSessionSheet";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import { getVisualsForType, themeMap } from "../plan/planTheme";
import { toBlocks } from "./sessionBlocks";
import { hasChartableTelemetry } from "../../utils/telemetry/summary";
import { machineEfficiency } from "../../utils/telemetry/efficiency";
import usePlanEditor from "../../hooks/usePlanEditor";
import CountsAsPicker from "./CountsAsPicker";
import {
  adaptationsForWorkoutType,
  readCountsAs,
} from "../../utils/trainingBalance";
import LogWorkoutDialog from "../session/LogWorkoutDialog";
import { consumeLogWorkoutRequest } from "../../utils/logWorkoutRequest";
import ImportFitDialog from "../session/ImportFitDialog";
import {
  describeActivity,
  isLoggedSession,
} from "../../utils/loggedSession";
import {
  buildPhaseStatsMap,
  correctedRepsById,
  formatPhaseSummary,
  formatPhaseDuration,
  chunkSplits,
} from "../../utils/phaseLog";
import useLocalStorage from "../../hooks/useLocalStorage";
import { isHiddenInSummary, formatLoadSummary } from "../../utils/planVisuals";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  ChevronRight,
  Footprints,
  Zap,
  Play,
  Trash2,
  Upload,
  Dumbbell,
  Loader2,
  AlertCircle,
  Activity,
  Heart,
  Pencil,
  Plus,
  PlusCircle,
  Watch,
  ChevronDown,
  MoreHorizontal,
  Share2,
  Search,
  Eye,
  EyeOff,
  ArrowRight,
  Medal,
} from "lucide-react";
import TelemetryChart from "./TelemetryChart";
import SessionFilterBar from "./SessionFilterBar";
import WeekOverview from "./WeekOverview";
import HeartRateZones from "./HeartRateZones";
import MachineSummary from "./MachineSummary";
import BadgeMedal from "../social/BadgeMedal";
import { BadgesCard, badgesForSession, useMyBadges } from "../social/Badges";
import ChallengesCard from "../social/ChallengesCard";
import RareEffort from "./RareEffort";
import MatesBoardCard from "../social/MatesBoardCard";
import { ZONE2_WEEKLY_MINUTES } from "../../config/programmePlanner";
import useProgress from "../progress/useProgress";
import ProgressSummaryCard from "../progress/ProgressSummaryCard";
import FirstVisitTip from "../help/FirstVisitTip";
import DetailSheet from "../session/generate/DetailSheet";
import { cn } from "../../lib/utils";
import {
  daysInLastSeven,
  groupSessionsByWeek,
  sessionKind,
  summariseLastSevenDays,
  weeksOnTarget,
} from "../../utils/activitySummary";
import { formatLapTime, lapSplits } from "../../utils/freeTraining";
import { computeWeeklyTargets } from "../../utils/weeklyTargets";
import { describeSessionTemperature } from "../../utils/sessionWeather";
import WeeklyTargetSummary from "./WeeklyTargetSummary";
import DraggableStepper from "../ui/draggable-stepper";
import { GOAL_CATEGORIES } from "../../config/goals";
import {
  EMPTY_FILTER,
  buildSessionQuery,
  describeFilter,
  filterSessionsLocally,
  isFilterActive,
  sessionDates,
  summariseSessions,
} from "../../utils/sessionFilters";

// One screen of history. Big enough that the common case — "what did I do this
// week" — needs no interaction, small enough that the panel stays a panel.
const PAGE_SIZE = 20;

// `datetime-local` wants a zone-less local "YYYY-MM-DDTHH:mm". Building it from
// the parts rather than via toISOString (which converts to UTC) is what stops an
// 8pm session being offered back for editing as 10am the next day.
const toLocalInputValue = (date) => {
  const d = new Date(date);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatMinutes = (min) => {
  if (!min || min < 1) return "< 1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem("completedSessions") || "[]");
  } catch {
    return [];
  }
};

const adjustStats = (stats, session, delta) => {
  const count = Math.max((stats.count || 0) + delta, 0);
  const totalMinutes = Math.max(
    (stats.total_minutes || 0) + delta * (Number(session?.duration_minutes) || 0),
    0,
  );
  return {
    ...stats,
    count,
    total_minutes: totalMinutes,
    avg_minutes: count > 0 ? Math.round(totalMinutes / count) : 0,
  };
};

// --- Flatten plan tree into leaf exercise nodes ---
// How many times a group runs its children. A time-capped group (AMRAP / "for
// time", unit s or m) carries a duration in `value`, not a round count.
const groupRounds = (node) => {
  const unit = String(node?.unit || "").toLowerCase();
  if (unit === "s" || unit === "m") return 1;
  const n = Math.floor(Number(node?.value));
  return Number.isFinite(n) && n > 1 ? n : 1;
};

// Each leaf carries `outerRounds`: how often the block AROUND its immediate
// parent repeated — 4 for a Tabata's work interval run as 4 sets of 8. The
// split breakdown uses it to lay one row per set (issue #82).
const flattenExercises = (node, depth = 0, outerRounds = 1, parentRounds = 1) => {
  if (!node) return [];
  // Hide time-only transitions (and zero-rest superset markers) — they're
  // queued at runtime but visual noise in the history summary (issue #22).
  if (isHiddenInSummary(node)) return [];
  if (!node.children || node.children.length === 0) {
    return [{ ...node, depth, outerRounds }];
  }
  const groupEntry = node.label ? [{ ...node, depth, isGroup: true }] : [];
  const childOuter = outerRounds * parentRounds;
  const rounds = groupRounds(node);
  return [
    ...groupEntry,
    ...node.children.flatMap((c) =>
      flattenExercises(c, depth + 1, childOuter, rounds),
    ),
  ];
};

// The recorded splits of a phase that ran more than once. Identical splits —
// every interval of a timed Tabata — collapse into the one-line summary on the
// row above; only splits that differ are worth a breakdown, and even then it
// stays folded until asked for, a row per set (issue #82).
const PhaseSplits = ({ stats, outerRounds }) => {
  const [open, setOpen] = useState(false);
  if (stats.uniform) return null;
  const rows = chunkSplits(stats.durations, outerRounds);
  const labelled = rows.length > 1;
  // The toggle holds the line's right end, so it never wraps onto a line of
  // its own at phone width (#168).
  return (
    <div>
      <div className="flex items-start gap-2 text-[10px] text-white/40">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 tabular-nums">
          <span>{stats.attemptCount} rounds</span>
          <span>·</span>
          <span>
            avg {formatPhaseDuration(stats.averageDurationSec)}
            {stats.stdDevSec > 0 ? ` ±${stats.stdDevSec}s` : ""}
          </span>
          <span>·</span>
          <span>
            {formatPhaseDuration(stats.minDurationSec)}
            {"–"}
            {formatPhaseDuration(stats.maxDurationSec)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Hide splits" : "Show splits"}
          className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-white/55 hover:text-white"
        >
          {open ? "Hide" : "Splits"}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (
        <div className="mt-1 space-y-1">
          {rows.map((row, ri) => (
            <div key={ri} className="flex items-start gap-1">
              {labelled && (
                <span className="text-[10px] text-white/40 w-10 shrink-0 py-0.5">
                  Set {ri + 1}
                </span>
              )}
              <div className="flex flex-wrap gap-1">
                {row.map((d, di) => (
                  <span
                    key={di}
                    className="text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded bg-white/5 text-white/60"
                  >
                    {formatPhaseDuration(d)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Session Detail Modal ---
const SessionDetailModal = ({
  session,
  badges,
  onClose,
  onSavePlan,
  onRerun,
  onDelete,
}) => {
  // Share opens a dialog, which can't sit above the bottom sheet, so the sheet
  // steps aside while it's open. Delete asks inside the sheet for the same
  // reason. Editing moves to its own dialog (the plan editor opens dialogs of
  // its own).
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // List endpoint omits the telemetry blob; lazy-load it when the user opens
  // the Telemetry tab. Kept while this session stays open, so re-tab-switching
  // is free; read only when it's this session's (the sheet is reused).
  const [loadedSession, setFullSession] = useState(null);
  const fullSession = loadedSession && String(loadedSession.id) === String(session?.id) ? loadedSession : null;
  const [tab, setTab] = useState("summary");
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  // Plan editing runs against a draft so an abandoned edit leaves the stored
  // session untouched — nothing is written until Save.
  const [draftPlan, setDraftPlan] = useState(null);
  const { planDisplayProps } = usePlanEditor(setDraftPlan);
  const { llmSettings } = useContext(WorkoutContext);
  // When/how-long/what-it-was-called, editable after the fact (#77). A session
  // logged from a description has its date READ out of the text, and an import
  // can land the wrong duration — both are worth being able to correct without
  // deleting the row and starting again.
  const [draftDetails, setDraftDetails] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const editing = draftDetails !== null;
  // Hide your own recorded times — on screen and from Copy Summary — so a
  // session can be passed to a friend as the plan alone (issue #82). Kept per
  // browser: someone who shares this way tends to want it every time.
  const [hideTimes, setHideTimes] = useLocalStorage("historyHideTimes", false);

  // If we already have telemetry inline (cloud-fresh session or local
  // backup), use that. Otherwise lazy-fetch when the tab opens. The tab only
  // shows when there's something to chart: a session run without a strap or
  // machine still saves a summary, and its tab was an empty chart.
  const inlineTelemetry = session?.telemetry || null;
  const hasTelemetry = hasChartableTelemetry(session);

  // A different session in the same modal must not inherit the previous one's
  // half-finished edit.
  useEffect(() => {
    setDraftPlan(null);
    setDraftDetails(null);
    setSaveError(null);
    setConfirmDelete(false);
    setShareOpen(false);
    setTab("summary");
    setFullSession(null);
  }, [session?.id]);

  useEffect(() => {
    if (tab !== "telemetry") return;
    if (inlineTelemetry || fullSession?.telemetry) return;
    if (!session?.id || String(session.id).startsWith("local-")) return;
    let cancelled = false;
    (async () => {
      setTelemetryLoading(true);
      try {
        const full = await api.getSession(session.id);
        if (!cancelled) setFullSession(full);
      } catch (err) {
        console.warn("[SessionHistory] telemetry fetch failed", err);
      } finally {
        if (!cancelled) setTelemetryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, session, inlineTelemetry, fullSession]);

  if (!session) return null;

  const planLabel =
    session.plan_data?.label || session.workout_type || "Workout";
  const activePlan = draftPlan || session.plan_data;
  const beginEdit = () => {
    // A record saved without a plan still gets an editor to add steps to.
    setDraftPlan(
      session.plan_data?.children
        ? session.plan_data
        : {
            ...(session.plan_data || {}),
            type: "Group",
            label: session.workout_type || "Workout",
            value: 1,
            children: [],
          },
    );
    setDraftDetails({
      // The name the list shows (#193): the plan's own, else the type.
      workout_type: session.plan_data?.label || session.workout_type || "",
      notes: session.notes || "",
      started_at: toLocalInputValue(session.started_at),
      duration_minutes: session.duration_minutes
        ? String(session.duration_minutes)
        : "",
    });
    setSaveError(null);
  };
  const cancelEdit = () => {
    setDraftPlan(null);
    setDraftDetails(null);
    setSaveError(null);
  };
  const commitEdit = async () => {
    setSavingPlan(true);
    setSaveError(null);
    const err = await onSavePlan(session, draftPlan, draftDetails);
    setSavingPlan(false);
    if (err) setSaveError(err);
    else cancelEdit();
  };
  const exercises = activePlan ? flattenExercises(activePlan) : [];

  // Build actual-time lookup from phase_log. Empty while times are hidden, so
  // every recorded-time element below drops out on the same switch.
  const phaseLog = (!hideTimes && activePlan?.phase_log) || [];
  const actualTimeMap = buildPhaseStatsMap(phaseLog);
  // Reps done per set where they differ from the prescription (#144). Behind
  // the same switch: hiding shows the session as prescribed.
  const repsDoneMap = correctedRepsById(phaseLog);

  const telemetrySamples = inlineTelemetry || fullSession?.telemetry || null;

  // HR avg/max for the summary tab. Prefer the 1 Hz telemetry summary; fall
  // back to the legacy ESP32 avg/max blob stored under plan_data.hr so older
  // sessions still surface their heart-rate stats.
  const hrSummary = (() => {
    const t = session.telemetry_summary?.hr;
    if (t && t.avg != null) return { avg: t.avg, min: t.min, max: t.max, zones: t.zones || null };
    const p = session.plan_data?.hr;
    if (p && p.avg_bpm != null) {
      return { avg: p.avg_bpm, min: p.min_bpm, max: p.max_bpm };
    }
    return null;
  })();

  // Badges this session earned (#204), from your badges list.
  const sessionBadges = badgesForSession(badges, session.started_at);

  const activity = session.activity || {};
  const metres = Number(activity.distance_m);
  const kcal = Number(activity.calories);
  const loggedHr = Number(activity.avg_hr);
  const temperature = describeSessionTemperature(session.telemetry_summary);
  const facts = [
    session.duration_minutes > 0 && ["Length", formatMinutes(session.duration_minutes)],
    session.rating != null && ["Effort", `${session.rating}/10`],
    metres > 0 && [
      "Distance",
      `${(metres / 1000).toFixed(metres >= 10000 ? 0 : 1)} km`,
    ],
    !hrSummary && loggedHr > 0 && ["Heart rate", `${Math.round(loggedHr)} bpm avg`],
    kcal > 0 && ["Energy", `${Math.round(kcal)} kcal`],
    temperature && ["Temperature", temperature],
    // Free training's laps (#146), as splits.
    Array.isArray(activity.laps) &&
      activity.laps.length > 0 && [
        "Laps",
        lapSplits(activity.laps)
          .map((lap) => formatLapTime(lap.split))
          .join(" · "),
      ],
  ].filter(Boolean);
  const sourceLabel = isLoggedSession(session)
    ? session.source === "garmin"
      ? "Imported from Garmin"
      : "Logged by you"
    : null;
  const subtitle = [
    formatDate(session.started_at),
    formatTime(session.started_at),
    sourceLabel,
  ]
    .filter(Boolean)
    .join(" · ");
  const copyPlan = hideTimes ? withoutRecordedTimes(activePlan) : activePlan;

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete?.(session);
    setDeleting(false);
    setConfirmDelete(false);
    onClose();
  };

  const ghost =
    "h-12 border-border bg-muted/40 text-base font-semibold hover:bg-muted/60";

  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="More actions"
          className={cn(ghost, "w-12 shrink-0")}
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      {/* Above the bottom sheet (z-60), which a default menu sits under. */}
      <DropdownMenuContent align="end" side="top" className="z-[70] w-60">
        {onSavePlan && (
          <DropdownMenuItem onSelect={beginEdit}>
            <Pencil className="mr-2 h-3.5 w-3.5 shrink-0" />
            <span className="flex flex-col">
              <span>Edit</span>
              <span className="text-[10px] text-muted-foreground">
                Name, counts as, time, length and exercises
              </span>
            </span>
          </DropdownMenuItem>
        )}
        {onSavePlan && <DropdownMenuSeparator />}
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() => setConfirmDelete(true)}
        >
          <Trash2 className="mr-2 h-3.5 w-3.5 shrink-0" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const footer = confirmDelete ? (
    <div className="grid gap-2">
      <p className="text-center text-sm text-muted-foreground">
        Remove &ldquo;{planLabel}&rdquo; from your history? This can&rsquo;t be
        undone.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outline"
          className={ghost}
          onClick={() => setConfirmDelete(false)}
          disabled={deleting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          className="h-12 text-base font-semibold"
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Delete
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex gap-2">
      {session.plan_data ? (
        <Button
          type="button"
          className="h-12 flex-1 text-base font-bold"
          onClick={() => {
            onRerun?.(session);
            onClose();
          }}
        >
          <Play className="mr-2 h-4 w-4 fill-current" />
          Run again
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          className={cn(ghost, "flex-1")}
          onClick={onSavePlan ? beginEdit : onClose}
        >
          {onSavePlan ? (
            <>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </>
          ) : (
            "Done"
          )}
        </Button>
      )}
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label="Share"
        title="Share"
        className={cn(ghost, "w-12 shrink-0")}
        onClick={() => setShareOpen(true)}
      >
        <Share2 className="h-5 w-5" />
      </Button>
      {moreMenu}
    </div>
  );

  // One step of "What you did" (#168). The type is the plan's coloured edge
  // rather than a pill, and the figures sit on the name's line, right-aligned,
  // so the row uses the sheet's full width. A setup or transition is a divider
  // line, not a row.
  const renderStep = (ex) => {
    if (ex.isGroup) {
      // A block nested inside a superset: a sub-heading inside its card.
      return (
        <div className="flex items-baseline justify-between gap-2 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-muted-foreground">
          <span className="min-w-0 break-words">{ex.label}</span>
          {groupRounds(ex) > 1 && <span className="shrink-0">{groupRounds(ex)}×</span>}
        </div>
      );
    }
    const stats = ex.id ? actualTimeMap[ex.id] : null;
    const isMulti = stats && stats.attemptCount > 1;
    const repsDone = ex.id ? repsDoneMap[ex.id] : null;
    const value = ex.value
      ? `${ex.value}${
          ex.unit === "s" ? "s" : ex.unit === "m" ? "m" : ex.unit ? ` ${ex.unit}` : ""
        }`
      : null;
    // Same figure the copied summary prints, from the same formatter — the
    // recap you read and the one you paste cannot disagree.
    const load = formatLoadSummary(ex);
    const took = stats && (!isMulti || stats.uniform) ? formatPhaseSummary(stats) : "";
    const { theme } = getVisualsForType(ex.type, ex.label);

    if (theme === themeMap.Transition) {
      const summary = formatPhaseSummary(stats);
      return (
        <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
          <span aria-hidden className="h-px flex-1 bg-white/10" />
          <ArrowRight aria-hidden className="h-3 w-3 shrink-0" />
          <span className="min-w-0 break-words">{ex.label}</span>
          {value && <span className="tabular-nums text-foreground/70">{value}</span>}
          {summary && <span className="tabular-nums text-white/40">took {summary}</span>}
          <span aria-hidden className="h-px flex-1 bg-white/10" />
        </div>
      );
    }

    const isRest = theme === themeMap.Rest;
    return (
      <div className="relative grid gap-0.5 py-2 pl-3.5 pr-3">
        <span
          aria-hidden
          className={cn("absolute bottom-2 left-0 top-2 w-[3px] rounded-r-sm", theme.bg)}
        />
        <div className="flex items-baseline gap-3">
          <span
            className={cn(
              "min-w-0 flex-1 break-words text-sm",
              isRest && "text-muted-foreground",
            )}
          >
            {ex.label}
          </span>
          {(value || load || took) && (
            <span className="flex max-w-[65%] flex-wrap items-baseline justify-end gap-x-1.5 text-right text-[13px] tabular-nums text-muted-foreground">
              {value && <span>{value}</span>}
              {load && <span className="font-semibold text-foreground">{load}</span>}
              {took && <span className="text-white/40">[{took}]</span>}
            </span>
          )}
        </div>
        {repsDone && (
          <div className="text-xs tabular-nums text-foreground/80">
            done {repsDone.join(" · ")}
          </div>
        )}
        {isMulti && <PhaseSplits stats={stats} outerRounds={ex.outerRounds} />}
      </div>
    );
  };

  const exerciseList = exercises.length > 0 && (
    <div>
      <div className="mb-1.5 flex items-center justify-between px-1">
        <h4 className="text-xs font-semibold text-muted-foreground">
          {editing ? "Exercises" : "What you did"}
        </h4>
        <button
          type="button"
          onClick={() => setHideTimes((h) => !h)}
          aria-pressed={!!hideTimes}
          title={
            hideTimes
              ? "Show your recorded times"
              : "Hide your recorded times, on screen and in Copy summary"
          }
          className="flex items-center gap-1 text-[11px] text-white/55 hover:text-white"
        >
          {hideTimes ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {hideTimes ? "Times hidden" : "Hide times"}
        </button>
      </div>
      <div className="grid gap-3.5">
        {toBlocks(exercises).map((block, bi) => {
          if (block.kind === "divider") {
            return <div key={bi}>{renderStep(block.ex)}</div>;
          }
          const card = (
            <div className="divide-y divide-white/[0.06] overflow-hidden rounded-lg bg-white/5">
              {block.rows.map((ex, ri) => (
                <React.Fragment key={ri}>{renderStep(ex)}</React.Fragment>
              ))}
            </div>
          );
          if (block.kind === "single") return <div key={bi}>{card}</div>;
          return (
            <div key={bi} className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-2 px-1 text-[13px] font-semibold">
                <span className="min-w-0 break-words">{block.ex.label}</span>
                {groupRounds(block.ex) > 1 && (
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">
                    {groupRounds(block.ex)}×
                  </span>
                )}
              </div>
              {block.rows.length > 0 && card}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Editing replaces the record of something that happened, so it gets a
  // dialog of its own with a confirmation on Save.
  if (editing) {
    return (
      <Dialog open onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
          <DialogHeader>
            <DialogTitle>Edit session</DialogTitle>
            <DialogDescription>
              Correct when it happened, how long it took, what it was called,
              what it counts as, or the exercises.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Name
              </Label>
              <Input
                value={draftDetails.workout_type}
                onChange={(e) =>
                  setDraftDetails((d) => ({ ...d, workout_type: e.target.value }))
                }
                className="h-9 text-sm"
              />
            </div>
            {/* Notes after the fact: "cut short, phone rang". */}
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Notes
              </Label>
              <Textarea
                value={draftDetails.notes}
                onChange={(e) => setDraftDetails((d) => ({ ...d, notes: e.target.value }))}
                placeholder="How it went, or why it was cut short"
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  When
                </Label>
                <Input
                  type="datetime-local"
                  value={draftDetails.started_at}
                  max={toLocalInputValue(new Date())}
                  onChange={(e) =>
                    setDraftDetails((d) => ({ ...d, started_at: e.target.value }))
                  }
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Duration (min)
                </Label>
                <Input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={draftDetails.duration_minutes}
                  onChange={(e) =>
                    setDraftDetails((d) => ({
                      ...d,
                      duration_minutes: e.target.value,
                    }))
                  }
                  className="h-9 text-sm tabular-nums"
                />
              </div>
            </div>
            {/* What it counts toward (#173): read from the name until you
                pick, then your pick is kept on the plan. */}
            <div className="space-y-1">
              <Label
                id="counts-as-label"
                className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Counts as
              </Label>
              <CountsAsPicker
                id="counts-as-label"
                value={
                  readCountsAs(draftPlan?.countsAs) ??
                  adaptationsForWorkoutType(
                    `${draftDetails.workout_type} ${draftPlan?.label || ""}`,
                  )
                }
                onChange={(countsAs) =>
                  setDraftPlan((p) => ({ ...(p || {}), countsAs }))
                }
              />
            </div>
            {/* The planner's own editor, not a load-only subset (#77): an
                imported or described session can land with phases wrong, out
                of order or ungrouped, and the fix shouldn't be deleting the
                row. The confirmation on Save marks the difference between
                drafting a plan and overwriting a record. */}
            {draftPlan && (
              <SessionPlanDisplay
                plan={draftPlan}
                editable
                {...planDisplayProps}
                llmSettings={llmSettings}
              />
            )}
            {saveError && <p className="px-1 text-xs text-destructive">{saveError}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" disabled={savingPlan} onClick={cancelEdit}>
              Cancel
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={savingPlan}>
                  {savingPlan && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Save changes
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Overwrite this session record?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This replaces the stored record of "{planLabel}": the plan
                    and any details you changed. The previous version isn't
                    kept, and this can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={commitEdit}>
                    Overwrite record
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <DetailSheet
        open={!shareOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={planLabel}
        description={subtitle}
        showDescription
        footer={footer}
      >
        {/* One quiet line of facts (#192), not a grid of boxes: the plan
            below is what this sheet is for. Length reads as itself. */}
        {facts.length > 0 && (
          <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {facts.map(([label, value]) => (
              <span key={label} className="whitespace-nowrap">
                {label !== "Length" && `${label} `}
                <span className="font-semibold tabular-nums text-foreground">{value}</span>
              </span>
            ))}
          </p>
        )}

        {session.notes && (
          <p className="border-l-[3px] border-border pl-2.5 text-sm italic text-muted-foreground">
            {`“${session.notes}”`}
          </p>
        )}

        {hasTelemetry && (
          <div className="flex w-fit items-center gap-1 rounded-full border border-white/15 p-0.5">
            {[
              ["summary", "Summary"],
              ["telemetry", "Telemetry"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={`flex items-center gap-1 rounded-full px-3 py-0.5 text-xs transition-colors ${
                  tab === value ? "bg-white/15 text-white" : "text-white/55 hover:text-white"
                }`}
              >
                {value === "telemetry" && <Activity className="h-3 w-3" />}
                {label}
              </button>
            ))}
          </div>
        )}

        {tab === "telemetry" &&
          (telemetryLoading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading telemetry…
            </div>
          ) : (
            <TelemetryChart
              samples={telemetrySamples}
              zones={session.telemetry_summary?.hr?.zones || null}
              phaseLog={activePlan?.phase_log || null}
              plan={activePlan}
            />
          ))}

        {tab === "summary" && session.rare_effort && (
          <RareEffort effort={session.rare_effort} className="px-1 text-sm" />
        )}

        {tab === "summary" && sessionBadges.length > 0 && (
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold text-muted-foreground">
              <Medal className="h-3 w-3 text-yellow-400" />
              Badges earned
            </h4>
            <div className="grid gap-2">
              {sessionBadges.map((b) => (
                <div key={b.key} className="flex items-center gap-2.5 px-1">
                  <BadgeMedal badge={b} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{b.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "summary" && hrSummary && (
          <div>
            <h4 className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold text-muted-foreground">
              <Heart className="h-3 w-3 text-red-500" />
              Heart rate
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Avg", value: hrSummary.avg },
                { label: "Min", value: hrSummary.min },
                { label: "Max", value: hrSummary.max },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-center rounded-lg bg-white/5 p-2"
                >
                  <span className="font-mono text-xl font-bold tabular-nums text-red-400">
                    {stat.value != null ? Math.round(stat.value) : "—"}
                  </span>
                  <span className="mt-0.5 text-[10px] uppercase tracking-widest text-white/40">
                    {stat.label} · bpm
                  </span>
                </div>
              ))}
            </div>
            {hrSummary.zones && (
              <div className="mt-3">
                <HeartRateZones zones={hrSummary.zones} />
              </div>
            )}
          </div>
        )}

        {tab === "summary" && session.telemetry_summary?.devices && (
          <MachineSummary
            devices={withEfficiency(session.telemetry_summary.devices, telemetrySamples)}
            machines={session.machines}
          />
        )}
        {tab === "summary" && exerciseList}
        {tab === "summary" && !session.plan_data && (
          <p className="text-sm text-muted-foreground">
            {sourceLabel ? `${sourceLabel}. ` : ""}No exercise list for this
            one.
          </p>
        )}
      </DetailSheet>

      <ShareSessionSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        session={session}
        samples={telemetrySamples}
        summaryPlan={copyPlan}
        // The recorded duration is a time too; hidden, the summary falls
        // back to the plan's own estimate.
        summaryMeta={{
          notes: session.notes,
          rating: session.rating,
          durationMinutes: hideTimes ? undefined : session.duration_minutes,
        }}
      />
    </>
  );
};

// Sessions saved before watts per beat, drift and metres per stroke were
// worked out get them from their samples, once the Telemetry tab has loaded.
const withEfficiency = (devices, samples) => {
  if (!samples) return devices;
  const out = { ...devices };
  Object.keys(out).forEach((type) => {
    const d = out[type];
    if (d.w_per_beat == null && d.m_per_stroke == null) out[type] = { ...d, ...(machineEfficiency(samples, type) || {}) };
  });
  return out;
};

// --- Stats Summary ---
// One row of history. The whole row opens the details sheet, where Run again,
// Copy, Share, Edit and Delete live — a page of rows each carrying four
// controls was eighty buttons competing with the things you scan for.
const KIND_STYLE = {
  strength: { icon: Dumbbell, className: "bg-red-400/10 text-red-400" },
  hard: { icon: Zap, className: "bg-amber-400/10 text-amber-400" },
  cardio: { icon: Heart, className: "bg-sky-400/10 text-sky-400" },
  other: { icon: Footprints, className: "bg-white/[0.07] text-muted-foreground" },
};

const SessionRow = ({ session, onView }) => {
  const planLabel =
    session.plan_data?.label || session.workout_type || "Workout";
  const { icon: Icon, className } = KIND_STYLE[sessionKind(session)];
  const meta = [
    formatDate(session.started_at),
    formatTime(session.started_at),
    session.duration_minutes ? formatMinutes(session.duration_minutes) : null,
    session.rating != null ? `Effort ${session.rating}/10` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const measured = describeActivity(session.activity);

  return (
    <button
      type="button"
      onClick={() => onView(session)}
      aria-label={`View details for ${planLabel}`}
      className="grid w-full grid-cols-[2rem_minmax(0,1fr)_1rem] items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
    >
      <span className={cn("grid h-8 w-8 place-items-center rounded-lg", className)}>
        <Icon className="h-[17px] w-[17px]" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-white">
          {planLabel}
        </span>
        <span className="mt-px block truncate text-xs tabular-nums text-muted-foreground">
          {meta}
        </span>
        {measured && (
          <span className="block truncate text-xs tabular-nums text-muted-foreground">
            {measured}
          </span>
        )}
        <RareEffort effort={session.rare_effort} className="mt-px" />
        {session.notes && (
          <span className="block truncate text-xs italic text-muted-foreground">
            {`“${session.notes}”`}
          </span>
        )}
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
    </button>
  );
};

// --- Import Dialog ---
// Controllable: the header offers this from a menu rather than its own button
// (three buttons crowding the title wrapped into a ragged stack on a phone), so
// `open`/`onOpenChange` can come from outside. Left uncontrolled it still
// renders its own trigger, which is how any other caller would use it.
const ImportDialog = ({ onImport, open: openProp, onOpenChange }) => {
  const [openState, setOpenState] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? onOpenChange : setOpenState;
  const [pasteText, setPasteText] = useState("");
  const [importError, setImportError] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    setImportError(null);
    if (!pasteText.trim()) {
      setImportError("Please paste a session plan to import.");
      return;
    }

    setImporting(true);
    const result = await onImport(pasteText.trim());
    setImporting(false);

    if (result.success) {
      setOpen(false);
      setPasteText("");
    } else {
      setImportError(result.error || "Failed to load session plan.");
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setPasteText("");
          setImportError(null);
        }
      }}
    >
      {!isControlled && (
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <Upload className="h-3.5 w-3.5" />
            Paste session plan
          </Button>
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Paste Session Plan</AlertDialogTitle>
          <AlertDialogDescription>
            Paste a session plan copied from Khronos ("{COPY_PLAN_LABEL}"). If
            the format is corrupted, AI will attempt to interpret it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder="Paste session plan here…"
          className="min-h-[200px] font-mono text-xs"
          rows={10}
        />
        {importError && (
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{importError}</span>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            onClick={handleImport}
            disabled={importing || !pasteText.trim()}
          >
            {importing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Upload className="h-4 w-4 mr-1" />
            )}
            {importing ? "Importing..." : "Import"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// --- Main Component ---
const SessionHistory = ({ title = "Workout history" }) => {
  const { loadSessionFromData, llmSettings, userProfile, setUserProfile } =
    useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const { sessions: recentSessions } = useUserProfile();
  const { programme } = useProgramme();
  const myBadges = useMyBadges();
  const [addOpen, setAddOpen] = useState(false);
  // "Change" on the weekly card: goal and days, drafted here and saved to the
  // Profile when the sheet closes (a drag on the stepper would otherwise
  // write on every step).
  const [targetDraft, setTargetDraft] = useState(null);
  const [olderSuggestionDismissed, setOlderSuggestionDismissed] = useLocalStorage(
    "olderStrengthSuggestionDismissed",
    false,
  );
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailSession, setDetailSession] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  // Opened straight away when the Session tab sent the user here to log one.
  useEffect(() => {
    if (consumeLogWorkoutRequest()) setLogOpen(true);
  }, []);
  const [fitOpen, setFitOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  // The list is a page of history, not all of it. `hasMore` follows the same
  // rule the feed uses: a full page probably means there is another behind it.
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState(null);
  const [streakDates, setStreakDates] = useState([]);

  const filtering = isFilterActive(filter);

  // The last 7 days and the weekly target come from all recent training, not
  // the filtered page below: they're facts about you, not about the search.
  // Goal-aware: the programme's goal and days, else the Profile's, else
  // worked out from the last 8 weeks (utils/weeklyTargets.js).
  const targets = useMemo(
    () => computeWeeklyTargets({ programme, userProfile, trainingDates: streakDates }),
    [programme, userProfile, streakDates],
  );
  const weeklyTarget = { days: targets.days, source: targets.daysSource };
  const overview = useMemo(
    () =>
      summariseLastSevenDays(
        isAuthenticated && recentSessions?.length ? recentSessions : readLocal(),
        {
          targets: targets.counts,
          zone2Target: targets.counts.aerobic > 0 ? ZONE2_WEEKLY_MINUTES[targets.family] : 0,
        },
      ),
    // readLocal is re-read when the page's own list changes (a new log).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, recentSessions, sessions, targets],
  );
  const onTarget = weeksOnTarget(streakDates, weeklyTarget.days);
  // Headline figures for the Progress card (#203).
  const { fitness, vo2max, ftp } = useProgress(sessions);

  const closeTargetSheet = () => {
    if (
      targetDraft &&
      (targetDraft.primaryGoal !== userProfile.primaryGoal ||
        (targetDraft.weeklySessions || 0) !== (userProfile.weeklySessions || 0))
    ) {
      setUserProfile({
        ...userProfile,
        primaryGoal: targetDraft.primaryGoal,
        weeklySessions: targetDraft.weeklySessions || 0,
      });
    }
    setTargetDraft(null);
  };

  const olderSuggestion =
    targets.olderAdultSuggestion && !olderSuggestionDismissed
      ? {
          onAccept: () =>
            setUserProfile({
              ...userProfile,
              weeklyTargetOverrides: {
                ...(userProfile.weeklyTargetOverrides || {}),
                resistance: 3,
              },
            }),
          onDismiss: () => setOlderSuggestionDismissed(true),
        }
      : null;
  const daysTrained = daysInLastSeven(streakDates);
  const weekGroups = useMemo(() => groupSessionsByWeek(sessions), [sessions]);
  // "Have they ever trained?" is not the same question as "does this filter
  // match anything?", and only the unfiltered answer should hide the controls.
  const hasAnyHistory = sessions.length > 0 || streakDates.length > 0;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        const query = buildSessionQuery(filter);
        // The list and its totals are fetched together: they answer the same
        // question, and letting them arrive separately is how a filtered list
        // ends up sitting under unfiltered numbers.
        const [data, summary] = await Promise.all([
          api.getSessions({ ...query, limit: PAGE_SIZE, offset: 0 }),
          api.getSessionStats(query).catch((err) => {
            console.warn("[Khronos] Session stats unavailable:", err);
            return null;
          }),
        ]);
        const page = Array.isArray(data) ? data : [];
        setSessions(page);
        setHasMore(page.length === PAGE_SIZE);
        if (summary) {
          setStats(summary);
          setStreakDates(sessionDates((summary.dates || []).map((d) => ({ started_at: d }))));
        } else {
          // Stats endpoint missing or erroring — describe the page rather
          // than showing nothing. The streak falls back to the page too:
          // understating it beats rendering a lost streak.
          setStats(summariseSessions(page));
          setStreakDates(sessionDates(page));
        }
      } else {
        const matched = filterSessionsLocally(readLocal(), filter);
        setSessions(matched.slice(0, PAGE_SIZE));
        setHasMore(matched.length > PAGE_SIZE);
        setStats(summariseSessions(matched));
        setStreakDates(sessionDates(readLocal()));
      }
    } catch (err) {
      console.error("[Khronos] Failed to fetch sessions:", err);
      setSessions([]);
      setHasMore(false);
      setStats(null);
    }
    setLoading(false);
  }, [isAuthenticated, filter]);

  // Refetches from offset 0 whenever the filter changes — a narrowed list that
  // kept its old offset would open on page three of a two-page result.
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      if (isAuthenticated) {
        const next = await api.getSessions({
          ...buildSessionQuery(filter),
          limit: PAGE_SIZE,
          offset: sessions.length,
        });
        const page = Array.isArray(next) ? next : [];
        // Guard against a session deleted between pages shifting the offset
        // and re-serving a row we already hold.
        setSessions((prev) => {
          const seen = new Set(prev.map((s) => s.id));
          return [...prev, ...page.filter((s) => !seen.has(s.id))];
        });
        setHasMore(page.length === PAGE_SIZE);
      } else {
        const matched = filterSessionsLocally(readLocal(), filter);
        const next = matched.slice(0, sessions.length + PAGE_SIZE);
        setSessions(next);
        setHasMore(matched.length > next.length);
      }
    } catch (err) {
      console.error("[Khronos] Failed to load more sessions:", err);
    }
    setLoadingMore(false);
  }, [isAuthenticated, filter, sessions.length]);

  const handleRerun = useCallback(
    (session) => {
      if (!session.plan_data) return;
      loadSessionFromData(session.plan_data);
    },
    [loadSessionFromData],
  );

  const handleDelete = useCallback(
    async (session) => {
      try {
        if (
          isAuthenticated &&
          session.id &&
          !String(session.id).startsWith("local-")
        ) {
          await api.deleteSession(session.id);
        } else {
          // Remove from localStorage
          const stored = JSON.parse(
            localStorage.getItem("completedSessions") || "[]",
          );
          const filtered = stored.filter((s) => s.id !== session.id);
          localStorage.setItem("completedSessions", JSON.stringify(filtered));
        }
        setSessions((prev) => prev.filter((s) => s.id !== session.id));
        // The tiles are computed server-side over the whole filtered set, so
        // dropping the row from the list alone would leave the count above it
        // one too high until the next refetch.
        setStats((prev) => (prev ? adjustStats(prev, session, -1) : prev));
      } catch (err) {
        console.error("[Khronos] Failed to delete session:", err);
      }
    },
    [isAuthenticated],
  );

  // Record a workout that happened outside Khronos (#77). Written through the
  // same endpoint a Coach session uses, so it lands in one history and reaches
  // the LLM prompt by the same route — the only difference is `source`.
  // Returns an error string on failure so the dialog can keep the typed entry.
  const handleLogWorkout = useCallback(
    async (payload) => {
      try {
        if (isAuthenticated) {
          const saved = await api.createSession(payload);
          setSessions((prev) => [saved, ...prev]);
          setStats((prev) => (prev ? adjustStats(prev, saved, +1) : prev));
          setStreakDates((prev) => sessionDates([
            { started_at: saved.started_at },
            ...prev.map((d) => ({ started_at: d })),
          ]));
        } else {
          const stored = JSON.parse(
            localStorage.getItem("completedSessions") || "[]",
          );
          const local = {
            ...payload,
            id: `local-${Date.now()}`,
            created_at: new Date().toISOString(),
          };
          stored.unshift(local);
          if (stored.length > 100) stored.length = 100;
          localStorage.setItem("completedSessions", JSON.stringify(stored));
          setSessions((prev) => [local, ...prev]);
          setStats((prev) => (prev ? adjustStats(prev, local, +1) : prev));
          setStreakDates(sessionDates(stored));
        }
        return null;
      } catch (err) {
        console.error("[Khronos] Failed to log workout:", err);
        return err.message || "Could not save. Check your connection.";
      }
    },
    [isAuthenticated],
  );

  // Interpret a free-text description into a plan tree (#77). Imported lazily
  // for the same reason handleImport does: llmApi drags in the ESM-only Gemini
  // client, and eager-importing it here would pull that into every render of
  // the history tab.
  const handleParseWorkout = useCallback(
    async (text, settings, signal) => {
      const llmApi = (await import("../../apis/llmApi")).default;
      return llmApi.parseLoggedWorkout(text, settings || llmSettings, signal);
    },
    [llmSettings],
  );

  // Persist an edit made in the detail modal: the plan's loads, the session's
  // own facts, or both. Returns an error string on failure (the modal keeps the
  // draft open so nothing typed is lost), or null on success. Local-backup
  // sessions — the ones written when a save could not reach the server — are
  // patched in localStorage under the same rules.
  const handleSavePlan = useCallback(
    async (session, nextPlan, details) => {
      const isLocal =
        !isAuthenticated || !session.id || String(session.id).startsWith("local-");

      // Build the patch from what actually changed and parses. A blank or
      // unusable field leaves the stored value alone rather than clearing it —
      // every column on the endpoint is COALESCE-guarded, and the UI should not
      // be the thing that writes a null into a session's start time.
      const patch = {};
      if (nextPlan) patch.plan_data = nextPlan;

      if (details) {
        const name = String(details.workout_type || "").trim();
        const shown = String(session.plan_data?.label || session.workout_type || "").trim();
        if (name && name !== shown) {
          patch.workout_type = name;
          // The list shows the plan's own name first, so a rename has to
          // reach it too, or the old name stays on the row (#193).
          const plan = patch.plan_data || session.plan_data;
          if (plan && typeof plan === "object") patch.plan_data = { ...plan, label: name };
        }

        if (details.notes != null) {
          const notes = String(details.notes).trim();
          if (notes !== (session.notes || "").trim()) patch.notes = notes;
        }

        const minutes = Math.round(Number(details.duration_minutes));
        const validMinutes = Number.isFinite(minutes) && minutes > 0;
        if (validMinutes) patch.duration_minutes = minutes;

        const startMs = new Date(details.started_at).getTime();
        if (Number.isFinite(startMs)) {
          if (startMs > Date.now() + 60 * 1000) {
            return "That start time is in the future.";
          }
          patch.started_at = new Date(startMs).toISOString();
          // ended_at is derived, never asked for: it is what marks a session as
          // having happened (see trainingHistory's sessionHappened), so moving
          // the start without moving the end would leave the row describing a
          // session that ran backwards.
          const span = validMinutes
            ? minutes
            : Number(session.duration_minutes) || 0;
          patch.ended_at = new Date(startMs + span * 60 * 1000).toISOString();
        }
      }

      if (Object.keys(patch).length === 0) return null;

      try {
        if (isLocal) {
          const stored = JSON.parse(
            localStorage.getItem("completedSessions") || "[]",
          );
          localStorage.setItem(
            "completedSessions",
            JSON.stringify(
              stored.map((s) => (s.id === session.id ? { ...s, ...patch } : s)),
            ),
          );
        } else {
          await api.updateSession(session.id, patch);
        }
        const merge = (s) => (s.id === session.id ? { ...s, ...patch } : s);
        setSessions((prev) => prev.map(merge));
        setDetailSession((prev) => (prev ? merge(prev) : prev));
        return null;
      } catch (err) {
        console.error("[Khronos] Failed to save session changes:", err);
        return err.message || "Could not save. Check your connection.";
      }
    },
    [isAuthenticated],
  );

  const handleImport = useCallback(
    async (text) => {
      // Try direct YAML parse first
      const plan = deserializeSession(text);
      if (plan) {
        loadSessionFromData(plan);
        return { success: true };
      }

      // Corrupted / non-YAML — try AI repair
      try {
        const llmApi = (await import("../../apis/llmApi")).default;
        const repaired = await llmApi.repairSession(text, llmSettings);
        if (repaired) {
          loadSessionFromData(repaired);
          return { success: true };
        }
        return {
          success: false,
          error: "AI could not interpret the pasted text as a workout session.",
        };
      } catch (err) {
        return {
          success: false,
          error: err.message || "Failed to repair session with AI.",
        };
      }
    },
    [loadSessionFromData, llmSettings],
  );

  const addOption = (icon, label, hint, onClick) => {
    const Icon = icon;
    return (
      <button
        type="button"
        onClick={() => {
          setAddOpen(false);
          // Let the sheet close before the next dialog opens.
          requestAnimationFrame(onClick);
        }}
        className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-3 rounded-xl border border-border bg-background p-3 text-left hover:border-primary/40"
      >
        <Icon className="h-[18px] w-[18px] text-muted-foreground" />
        <span>
          <span className="block text-sm font-semibold text-white">{label}</span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-5 pb-8 pt-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start lg:gap-x-8">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-5 lg:sticky lg:top-20">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[22px] font-bold text-white">{title}</h2>
          <Button
            variant="outline"
            onClick={() => setAddOpen(true)}
            className="h-9 gap-1.5 border-border bg-card px-3"
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
        {hasAnyHistory && (
          <FirstVisitTip id="activity" title="Your week at a glance" topic="activity#last-7-days">
            The card below compares your last 7 days with your weekly targets.
            Tap Change on it to set your goal and how many days you train.
          </FirstVisitTip>
        )}
        {hasAnyHistory && (
          <WeekOverview
            summary={overview}
            target={weeklyTarget}
            weeksOnTarget={onTarget}
            daysTrained={daysTrained}
            onChangeTarget={() =>
              setTargetDraft({
                primaryGoal: userProfile.primaryGoal || "General Fitness",
                weeklySessions: userProfile.weeklySessions || 0,
              })
            }
            suggestion={olderSuggestion}
          />
        )}
        {/* Your badges' home (#204). */}
        <BadgesCard data={myBadges} />
        <ChallengesCard />
        <MatesBoardCard />
        {hasAnyHistory && <ProgressSummaryCard fitness={fitness} vo2max={vo2max} ftp={ftp} />}
      </div>

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-4">
        {/* Shown whenever there is any history at all, including none matching
            — hiding the search box on an empty result is how you strand
            someone inside a filter they can no longer see or undo. */}
        {(hasAnyHistory || filtering) && (
          <SessionFilterBar
            filter={filter}
            onChange={setFilter}
            resultCount={stats?.count ?? sessions.length}
            loading={loading}
          />
        )}

        {/* A filtered view totals what it shows, like a filtered statement. */}
        {filtering && stats && !loading && sessions.length > 0 && (
          <p className="-mt-1 px-1 text-xs text-muted-foreground tabular-nums">
            {describeFilter(filter) ? `${describeFilter(filter)} · ` : ""}
            {stats.count} {stats.count === 1 ? "session" : "sessions"} ·{" "}
            {formatMinutes(stats.total_minutes)}
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : sessions.length === 0 ? (
          // Two different nothings: a history you haven't started, and a
          // filter that happens to match none of it. The first wants
          // encouragement, the second wants a way out.
          filtering ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No sessions match those filters.</p>
              <p className="text-xs mt-1">
                Try a different word, or widen the date range.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setFilter(EMPTY_FILTER)}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border py-10 text-center text-muted-foreground">
              <Dumbbell className="mx-auto mb-3 h-8 w-8 opacity-40" />
              <p className="text-sm">No sessions yet.</p>
              <p className="mt-1 text-xs">
                Finish a workout in the Coach, or log one you did elsewhere.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setLogOpen(true)}
              >
                Log a workout
              </Button>
            </div>
          )
        ) : (
          <>
            {weekGroups.map((group) => (
              <div key={group.key} className="grid gap-1.5">
                <div className="flex items-baseline justify-between gap-2 px-0.5">
                  <span className="text-[13px] font-bold text-white">
                    {group.title}
                    <span className="font-normal text-muted-foreground">
                      {" · "}
                      {group.range}
                    </span>
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {group.sessions.length} · {formatMinutes(group.minutes)}
                  </span>
                </div>
                <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
                  {group.sessions.map((session) => (
                    <SessionRow
                      key={session.id}
                      session={session}
                      onView={setDetailSession}
                    />
                  ))}
                </div>
              </div>
            ))}
            {hasMore && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Load more
              </Button>
            )}
            {/* Without this, a list that has run out looks identical to one
                still loading — and "is that everything?" is exactly the
                question a paged history creates. */}
            {!hasMore && sessions.length > PAGE_SIZE && (
              <p className="text-center text-xs text-muted-foreground">
                That's all {stats?.count ?? sessions.length} of them.
              </p>
            )}
          </>
        )}
      </div>

      <SessionDetailModal
        session={detailSession}
        badges={myBadges?.badges}
        onClose={() => setDetailSession(null)}
        onSavePlan={handleSavePlan}
        onRerun={handleRerun}
        onDelete={handleDelete}
      />

      <DetailSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add to your history"
      >
        <div className="grid gap-2">
          <p className="text-xs font-semibold text-muted-foreground">
            Record what you did
          </p>
          {addOption(
            PlusCircle,
            "Log a workout",
            "Describe it in your own words, or fill it in",
            () => setLogOpen(true),
          )}
          {addOption(Watch, "Import from Garmin", ".fit files or a full export", () =>
            setFitOpen(true),
          )}
        </div>
        <div className="grid gap-2">
          <p className="text-xs font-semibold text-muted-foreground">
            Load a plan to run
          </p>
          {addOption(
            Upload,
            COPY_PLAN_LABEL.replace("Copy ", "Paste "),
            "Opens it on the Session tab",
            () => setPasteOpen(true),
          )}
        </div>
      </DetailSheet>

      <DetailSheet
        open={!!targetDraft}
        onOpenChange={(open) => {
          if (!open) closeTargetSheet();
        }}
        title="Your weekly target"
        description="Your goal and how often you train set the targets above."
        showDescription
      >
        {targetDraft && (
          <>
            {programme && (
              <p className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                While your programme runs, its goal and schedule set the
                targets. These apply again when it ends.
              </p>
            )}
            <DraggableStepper
              label="Days a week"
              value={targetDraft.weeklySessions || 0}
              onChange={(v) => setTargetDraft((d) => ({ ...d, weeklySessions: v }))}
              min={0}
              max={14}
              step={1}
              unit="days"
              offLabel="Auto"
            />
            <WeeklyTargetSummary
              profile={{ ...userProfile, ...targetDraft }}
              className="rounded-lg border border-border bg-muted/20 px-3 py-2"
            />
            {Object.entries(GOAL_CATEGORIES).map(([category, goals]) => (
              <div key={category} className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {category}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {goals.map((goal) => (
                    <button
                      key={goal}
                      type="button"
                      aria-pressed={targetDraft.primaryGoal === goal}
                      onClick={() => setTargetDraft((d) => ({ ...d, primaryGoal: goal }))}
                      className={cn(
                        "rounded-full border px-3 py-2 text-[13px] transition-colors",
                        targetDraft.primaryGoal === goal
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-background text-foreground hover:border-primary/40",
                      )}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </DetailSheet>

      <LogWorkoutDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        onSave={handleLogWorkout}
        onParse={handleParseWorkout}
        llmSettings={llmSettings}
      />

      <ImportFitDialog
        open={fitOpen}
        onOpenChange={setFitOpen}
        existingSessions={sessions}
        onImport={handleLogWorkout}
      />

      <ImportDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        onImport={handleImport}
      />
    </div>
  );
};

export default SessionHistory;
